import { ActionModel, GroupedMessagesByTimestamp } from '@klant/conversational-connector/klant-conversational-connector.model';
import { ChatMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';
import { DateGroupService } from '@klant/conversational/date-group-service/date-group-service';

import { Component, computed, input, OnInit, output, ViewChild, ElementRef, effect, inject } from '@angular/core';

import { ApContentService } from '@ap/content';

import content from './klant-conversational.content.json';

@Component({
    selector: 'klant-conversational',
    templateUrl: './klant-conversational.component.html',
    standalone: false
})
export class KlantConversationalComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly dateGroupService = inject(DateGroupService);

    @ViewChild('scrollChatContainer') scrollChatContainer!: ElementRef;
    @ViewChild('chatInputContainer', { read: ElementRef, static: false }) chatInputContainer?: ElementRef;

    public liveMessage1 = input<string>('');
    public liveMessage2 = input<string>('');
    public messageLoading = input<boolean>();
    public chatMessages = input<ChatMessageModel[]>();
    public firstMessageWasSent = input<boolean>();
    public sendAction = output<ActionModel>();
    public sendMessage = output<string>();
    public firstMessageSent = output<boolean>();

    constructor() {
        this.setupAutoScrollBehavior();
    }

    public groupedMessagesByDate = computed(() => {
        const messages = this.chatMessages() || [];

        return this.dateGroupService.groupChatMessageByDate(messages);
    });

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
    }

    public showAvatar(currentMessage: ChatMessageModel, index: number, groupedMessages: GroupedMessagesByTimestamp): boolean {
        if (currentMessage.fromClient) {
            return false;
        }

        if (index === 0) {
            return true;
        }

        const previousMessage = groupedMessages.chatMessages[index - 1];

        return previousMessage.fromClient || previousMessage.type === TypeModel.PARTICIPANT_MESSAGE;
    }

    public showMessageLoaderAvatar(): boolean {
        const previousMessage = this.chatMessages()[this.chatMessages().length - 1];

        return previousMessage.fromClient || (previousMessage.type === TypeModel.PARTICIPANT_MESSAGE && !!previousMessage.introductionText);
    }

    public botOrLivechatAvatar(): string {
        if (this.isChattingWithAgent()) {
            return 'avatar-agent';
        }

        return 'avatar-bot';
    }

    private isChattingWithAgent(): boolean {
        const botOrAgentMessages: ChatMessageModel[] = this.chatMessages().filter((message) => !message.fromClient);

        const lastMessageFromBotOrAgent = botOrAgentMessages[botOrAgentMessages.length - 1];

        return (
            (lastMessageFromBotOrAgent.type === TypeModel.PARTICIPANT_MESSAGE ||
                lastMessageFromBotOrAgent.type === TypeModel.PLAIN_TEXT_MESSAGE) &&
            lastMessageFromBotOrAgent.serviceName === 'livechat'
        );
    }

    public isLastMessage(currentMessage: ChatMessageModel): boolean {
        const lastMessage = this.chatMessages()[this.chatMessages().length - 1];
        return currentMessage.transactionId === lastMessage.transactionId;
    }

    public onSendAction(action: ActionModel): void {
        this.sendAction.emit(action);
        this.setFocusToInputAfterDisappearingButton();
    }

    public onSendMessage(message: string): void {
        this.sendMessage.emit(message);
    }

    public onFirstMessageSent(isFirst: boolean): void {
        return this.firstMessageSent.emit(isFirst);
    }

    private setupAutoScrollBehavior(): void {
        effect(() => {
            if (this.messageLoading() || this.groupedMessagesByDate()?.length) {
                this.scrollToBottomDelayed();
            }
        });
    }

    private scrollToBottomDelayed(): void {
        setTimeout(() => {
            const container = this.scrollChatContainer?.nativeElement;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        }, 100);
    }

    private setFocusToInputAfterDisappearingButton(): void {
        const attemptFocus = (attempts = 0) => {
            if (attempts > 10) {
                return;
            }

            if (!this.chatInputContainer?.nativeElement) {
                requestAnimationFrame(() => attemptFocus(attempts + 1));
                return;
            }

            const inputSelector = this.chatInputContainer.nativeElement.querySelector('ap-textarea textarea') as HTMLElement;

            if (inputSelector) {
                inputSelector.focus();
            } else {
                requestAnimationFrame(() => attemptFocus(attempts + 1));
            }
        };

        attemptFocus();
    }
}
